import type { LibraryContentType } from '../types';
import type { BookmarkableTypes } from '~/elc/features/learner-saved-content/types';

export const LIBRARY_ROUTES = {
  home: '/libraries',
  library: '/libraries/:id',
} as const;

export const LIBRARY_API_ROOT_URL = 'libraries';
export const UNIVERSITY_API_ROOT_URL = 'universities';

export enum LibrariesSortOrder {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
  DEFAULT = 'DEFAULT',
}

export enum LibraryContentTypes {
  FOLDER = 'folder',
  GUIDE = 'guide',
  SERIES = 'series',
  TOP_LEVEL_CONTENT = 'top_level_content',
  CHALLENGE = 'challenge',
  RESOURCE = 'resource',
  CMS_LINK = 'cms_link',
  CMS_FILE = 'cms_file',
  LINKEDIN_LEARNING = 'linkedin_learning',
  UDEMY = 'udemy',
}

export enum EmptyPageVariants {
  NO_SEARCH_RESULTS = 'NO_SEARCH_RESULTS',
  NO_FILTERS_RESULTS = 'NO_FILTERS_RESULTS',
  EMPTY_LIBRARY_ADMIN_VIEW = 'EMPTY_LIBRARY_ADMIN_VIEW',
  EMPTY_FOLDER_ADMIN_VIEW = 'EMPTY_FOLDER_ADMIN_VIEW',
  EMPTY_LIBRARY_LEARNER_VIEW = 'EMPTY_LIBRARY_LEARNER_VIEW',
  EMPTY_FOLDER_LEARNER_VIEW = 'EMPTY_FOLDER_LEARNER_VIEW',
  NO_LIBRARIES = 'NO_LIBRARIES',
}

export const enum EditModalType {
  NEW = 'new',
  EDIT = 'edit',
}

export const enum EditModalFocus {
  TITLE = 'title',
  DESCRIPTION = 'description',
  IMAGE = 'image',
}

export const DEFAULT_SORT_KEY = 'uiOrder';

type APIResource = {
  [P in LibraryContentTypes]: string;
};

// CMS links/files are top_level_content. When we interact with the library to add/remove them, we therefore use the
// same APIs as other top_level_content, so these values can be omitted from the APIResource type
type CMSAssetType = LibraryContentTypes.CMS_FILE | LibraryContentTypes.CMS_LINK;

export const APIResourceForContentType: Omit<APIResource, CMSAssetType> = {
  [LibraryContentTypes.FOLDER]: 'folders',
  [LibraryContentTypes.GUIDE]: 'guides',
  [LibraryContentTypes.CHALLENGE]: 'challenges',
  [LibraryContentTypes.RESOURCE]: 'resources',
  [LibraryContentTypes.SERIES]: 'series',
  [LibraryContentTypes.TOP_LEVEL_CONTENT]: 'top_level_content',
  [LibraryContentTypes.LINKEDIN_LEARNING]: 'linkedin_learning',
  [LibraryContentTypes.UDEMY]: 'udemy',
};

const { t } = window.I18n;

export const translatedContentTypes: { [type in LibraryContentType]: string } = {
  [LibraryContentTypes.FOLDER]: t('common.content.folder.one'),
  [LibraryContentTypes.GUIDE]: t('common.content.guide.one'),
  [LibraryContentTypes.SERIES]: t('common.content.path.one'),
  [LibraryContentTypes.TOP_LEVEL_CONTENT]: t('common.content.scorm.one'),
  [LibraryContentTypes.CHALLENGE]: t('common.content.challenge.one'),
  [LibraryContentTypes.RESOURCE]: t('common.content.resource.one'),
  [LibraryContentTypes.CMS_LINK]: t('js.admin.cms.link'),
  [LibraryContentTypes.CMS_FILE]: t('js.admin.cms.file'),
  [LibraryContentTypes.LINKEDIN_LEARNING]: t('common.content.linkedin_learning.one'),
  [LibraryContentTypes.UDEMY]: t('common.content.udemy.one'),
};

export enum LibraryTopLevelContentTypes {
  SCORM = 'scorm',
  CMS_FILE = 'cms_file',
  CMS_LINK = 'cms_link',
  CONTENT_PROVIDER_CONTENT = 'content_provider_content',
}

export const DEFAULT_SLEEP_MS = 2000;

export enum MixpanelEvents {
  CHANGE_PAGE_LAYOUT = 'learner_library_page_change_layout',
  SEARCH = 'learner_library_page_search_applied',
  FILTER = 'learner_library_page_filters_applied',
  VIEW_ITEM = 'learner_library_page_view_item',
  FEATURED_CONTENT = 'library_featured_content',
  DOWNLOAD_FILE = 'cms_learner_library_page_download_asset',
  FILE_VIEWER_DOWNLOAD = 'cms_preview_page_download_asset',
  COPY_LINK = 'cms_learner_library_page_copy_asset',
  EXTERNAL_VIEW_VIEWER_DOWNLOAD = 'cms_external_view_page_download_asset',
}

export const LIBRARY_BOOKMARK_TYPE_MAP: Record<LibraryContentType, BookmarkableTypes> = {
  guide: 'guide',
  challenge: 'challenge',
  top_level_content: 'scorm',
  resource: 'resource',
  folder: 'university_folder',
  series: 'training_series',
  cms_file: 'cms_file',
  cms_link: 'cms_link',
  linkedin_learning: 'scorm',
  udemy: 'scorm',
};
